import React from 'react';
import Layout from '../components/layout/Layout';
import useProfile from '../hooks/useProfile';
import { SocialLink } from '../services/api';

const AboutPage: React.FC = () => {
  const { profile, loading, error } = useProfile();

  const getPlatformIcon = (platform: string) => {
    const lowerPlatform = platform.toLowerCase();
    
    switch (lowerPlatform) {
      case 'instagram':
        return '📸';
      case 'facebook':
        return '👤';
      case 'twitter':
        return '🐦';
      case 'linkedin':
        return '💼';
      case 'pinterest':
        return '📌';
      case 'youtube':
        return '📹';
      case 'tiktok':
        return '🎵';
      case 'behance':
        return '🎨';
      case 'dribbble':
        return '🏀';
      case 'artstation':
        return '🖼️';
      case 'deviantart':
        return '🎭';
      case 'website':
        return '🌐';
      default:
        return '🔗';
    }
  };

  const SocialLinks: React.FC<{ links: SocialLink[] }> = ({ links }) => {
    if (links.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-4 my-8">
        {links.map((link, index) => (
          <a
            key={index}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            <span>{getPlatformIcon(link.platform)}</span>
            <span>{link.platform}</span>
          </a>
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="bg-light">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl font-black mb-12 text-black">About Me</h1>

          {loading ? (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-black"></div>
            </div>
          ) : error ? (
            <div className="border-2 border-black text-black px-4 py-3 mb-6">
              <p>{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <div className="md:col-span-2">
                <div className="bg-white p-8 border-2 border-black">
                  <h2 className="text-2xl font-bold mb-6 text-black">Bio</h2>
                  {profile?.bio ? (
                    <div className="prose max-w-none">
                      {profile.bio.split('\n').map((paragraph, index) => (
                        <p key={index} className="mb-4 text-lg text-gray-700 leading-relaxed">{paragraph}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No bio has been added yet.</p>
                  )}

                  <SocialLinks links={profile?.socialLinks || []} />
                </div>
              </div>

              <div>
                <div className="bg-white p-8 border-2 border-black">
                  <h2 className="text-2xl font-bold mb-6 text-black">Contact Information</h2>

                  {profile?.contactInfo ? (
                    <div className="space-y-6">
                      {profile.contactInfo.email && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-600 mb-1">Email</h3>
                          <p className="text-lg">
                            <a
                              href={`mailto:${profile.contactInfo.email}`}
                              className="text-black hover:opacity-70 underline font-semibold"
                            >
                              {profile.contactInfo.email}
                            </a>
                          </p>
                        </div>
                      )}

                      {profile.contactInfo.phone && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-600 mb-1">Phone</h3>
                          <p className="text-lg">
                            <a
                              href={`tel:${profile.contactInfo.phone}`}
                              className="text-black hover:opacity-70 underline font-semibold"
                            >
                              {profile.contactInfo.phone}
                            </a>
                          </p>
                        </div>
                      )}

                      {profile.contactInfo.address && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-600 mb-1">Address</h3>
                          <p className="text-lg">{profile.contactInfo.address}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No contact information has been added yet.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AboutPage;